/**
 * TemplateModal — Modal para crear y editar plantillas de documentos de venta.
 * Formulario vertical con secciones: Datos de la Plantilla, Encabezado, Condiciones de Pago, Lineas, Observaciones.
 * Mismos patrones de estilo que DocumentModal.tsx pero sin emisión, estado ni referencias.
 */
import { useEffect } from 'react'
import { formatRut } from '@/lib/utils'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import { Autocomplete } from '@/components/Autocomplete'
import type { AutocompleteItem } from '@/components/Autocomplete'
import { NumericInput } from '@/components/NumericInput'
import { useTenantLocale, formatMoneyForTenant, tenantCurrency } from '@/lib/locale'
import { totalesDelDocumento, subtotalDeLinea } from '../lib/documentTotals'
import { useTenantTax } from '@/modules/config/hooks/useConfig'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import type {
  DocumentTemplate,
  SaleDocumentLine,
  DocumentPersonalizado,
  CustomerListItem,
  ProductListItem,
} from '@/modules/sales/hooks/useSales'
import { ProductAutocomplete } from './ProductAutocomplete'
import { Modal } from '@/components/ds/Modal'

interface TemplateModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  isPending: boolean
  initial?: Partial<DocumentTemplate> | null
  customers: CustomerListItem[]
}

// Compact styles (same as DocumentModal)
const inputCls =
  'w-full px-2.5 py-1.5 text-xs border border-input-border rounded-md bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200/30 focus:border-primary-200 transition-all placeholder:text-content-subtle'
const labelCls = 'block text-xs font-medium text-content-muted mb-0.5'
const errorCls = 'mt-0.5 text-xs text-danger-solid'
const sectionTitleCls = 'text-xs font-semibold text-content-subtle uppercase tracking-wider mb-2'

const DOCUMENT_TYPES = [
  { value: 'FE', label: 'Factura Electrónica' },
  { value: 'BE', label: 'Boleta Electrónica' },
  { value: 'NC', label: 'Nota de Crédito' },
  { value: 'ND', label: 'Nota de Débito' },
  { value: 'FC', label: 'Factura de Compra' },
]

const UNITS = ['UN', 'KG', 'MT', 'LT', 'HR', 'DY', 'MO', 'BX', 'PK', 'OT']

const EMPTY_LINE: SaleDocumentLine = {
  sku: '',
  name: '',
  description: '',
  unit: 'UN',
  quantity: '1',
  unit_price: '0',
  price_currency: '',  // vacio = la moneda base del tenant
  discount_pct: '0',
  is_tax_exempt: false,
  tax_data: {},
}

const PAYMENT_TERM_PRESETS = [30, 60, 90]

interface TemplateFormData {
  name: string
  description: string
  document_type: string
  customer: string
  payment_method: 'contado' | 'credito'
  payment_term_days: number
  lines: SaleDocumentLine[]
  notes: string
}

const formatCLP = (val: number) =>
  formatMoneyForTenant(val)

export function TemplateModal({
  open,
  onClose,
  onSubmit,
  isPending,
  initial,
  customers,
}: TemplateModalProps) {
  // El impuesto sale de Configuración -> Localización, no de una prop con `19`
  // de default: así el nombre sigue al país (IVA en Chile, IGV en Perú).
  const tax = useTenantTax()
  const taxRate = tax.rate
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TemplateFormData>({
    defaultValues: {
      name: '',
      description: '',
      document_type: 'FE',
      customer: '',
      payment_method: 'contado',
      payment_term_days: 0,
      lines: [{ ...EMPTY_LINE }],
      notes: '',
    },
  })

  const {
    fields: lineFields,
    append: appendLine,
    remove: removeLine,
  } = useFieldArray({ control, name: 'lines' })
  const locale = useTenantLocale()

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      reset({
        name: initial?.name || '',
        description: initial?.description || '',
        document_type: initial?.document_type || 'FE',
        customer: initial?.customer || '',
        payment_method: initial?.payment_method || 'contado',
        payment_term_days: initial?.payment_term_days || 0,
        lines:
          initial?.lines && initial.lines.length > 0
            ? initial.lines
            : [{ ...EMPTY_LINE }],
        notes: initial?.notes || '',
      })
    }
  }, [open, initial, reset])


  const watchedLines = watch('lines')
  const watchedPaymentMethod = watch('payment_method')
  const watchedDocType = watch('document_type')

  // Calculate totals in real time
  // Las plantillas no llevan moneda por línea, así que acá no hay conversión:
  // lo que aporta `documentTotals` es redondear el subtotal POR LÍNEA como el
  // backend, en vez de sumar los decimales y redondear al final.
  const opcionesDeTotales = { baseCurrency: tenantCurrency(), taxRate }
  const { montoNeto, montoExento, montoIVA, total } =
    totalesDelDocumento(watchedLines as any, opcionesDeTotales)
  const isFC = watchedDocType === ('FC' as string)

  // Auto-fill line from selected product
  const fillProductInLine = (index: number, product: ProductListItem) => {
    setValue(`lines.${index}.sku`, product.sku)
    setValue(`lines.${index}.name`, product.name)
    setValue(`lines.${index}.unit`, product.unit)
    setValue(`lines.${index}.unit_price`, product.unit_price)
    setValue(`lines.${index}.is_tax_exempt`, product.is_tax_exempt)
  }

  // Autocomplete items for products (by SKU and by Name)


  if (!open) return null

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={initial?.id ? 'Editar plantilla' : 'Nueva plantilla de documento'}
      actions={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="plantilla-documento-form"
            disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-1.5 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors"
          >
            {isPending && <Loader2 size={13} className="animate-spin" />}
            {initial?.id ? 'Guardar cambios' : 'Crear plantilla'}
          </button>
        </>
      }
    >
      {/* El pie lo pinta el Modal fuera de este <form>: el botón de guardar
          lo alcanza por `form=`. */}
      <form
        id="plantilla-documento-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="space-y-5">
          {/* -- Seccion: Datos de la Plantilla -- */}
          <div>
            <p className={sectionTitleCls}>Datos de la Plantilla</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 sm:col-span-4">
                <label htmlFor="templatemodal-name" className={labelCls}>
                  Nombre <span className="text-danger-solid">*</span>
                </label>
                <input id="templatemodal-name"
                  {...register('name', {
                    required: 'El nombre es obligatorio.',
                    maxLength: {
                      value: 200,
                      message: 'Máximo 200 caracteres.',
                    },
                  })}
                  placeholder="Ej: Factura mensual servicios TI"
                  className={inputCls}
                />
                {errors.name && (
                  <p className={errorCls}>{errors.name.message}</p>
                )}
              </div>
              <TextField
                dense
                className="col-span-2 sm:col-span-4"
                label="Descripción"
                multiline
                rows={2}
                placeholder="Descripción opcional de la plantilla..."
                inputProps={register('description')}
              />
            </div>
          </div>

          {/* -- Seccion: Encabezado del Documento -- */}
          <div>
            <p className={sectionTitleCls}>Encabezado del Documento</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SelectField
                dense
                className="col-span-2"
                label="Tipo de documento"
                selectProps={register('document_type')}
              >
                {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                {t.label}
                </option>
                ))}
              </SelectField>
              <div className="col-span-2">
                <label htmlFor="templatemodal-customer" className={labelCls}>Cliente</label>
                <Controller
                  name="customer"
                  control={control}
                  render={({ field }) => {
                    const customerItems: AutocompleteItem[] = customers.map(
                      (c) => ({
                        id: c.id,
                        label: c.legal_name,
                        sublabel: c.tax_id
                          ? formatRut(c.tax_id)
                          : undefined,
                      })
                    )
                    const selectedCustomer = customers.find(
                      (c) => c.id === field.value
                    )
                    return (
                      <Autocomplete id="templatemodal-customer"
                        items={customerItems}
                        value={field.value}
                        displayValue={
                          selectedCustomer
                            ? `${selectedCustomer.legal_name}${selectedCustomer.tax_id ? ` (${formatRut(selectedCustomer.tax_id)})` : ''}`
                            : ''
                        }
                        onChange={field.onChange}
                        placeholder="Buscar cliente por nombre o RUT..."
                        className={inputCls}
                      />
                    )
                  }}
                />
              </div>
            </div>
          </div>

          {/* -- Seccion: Condiciones de Pago -- */}
          <div>
            <p className={sectionTitleCls}>Condiciones de Pago</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Método de pago</label>
                <div className="flex gap-3 mt-1">
                  <label className="inline-flex items-center gap-1.5 text-xs text-content-muted cursor-pointer select-none">
                    <input
                      type="radio"
                      value="contado"
                      {...register('payment_method')}
                      className="h-3.5 w-3.5 border-divider-strong text-primary focus:ring-primary"
                    />
                    Contado
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-xs text-content-muted cursor-pointer select-none">
                    <input
                      type="radio"
                      value="credito"
                      {...register('payment_method')}
                      className="h-3.5 w-3.5 border-divider-strong text-primary focus:ring-primary"
                    />
                    Crédito
                  </label>
                </div>
              </div>

              {watchedPaymentMethod === 'credito' && (
                <div className="col-span-2">
                  <label className={labelCls}>Plazo (días)</label>
                  <div className="flex items-center gap-2 mt-1">
                    {PAYMENT_TERM_PRESETS.map((days) => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setValue('payment_term_days', days)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                          watch('payment_term_days') === days
                            ? 'bg-primary-50 border-primary-200 text-primary-hover'
                            : 'bg-surface border-divider text-content-muted hover:bg-soft'
                        }`}
                      >
                        {days}d
                      </button>
                    ))}
                    <input aria-label="Plazo de pago en días"
                      type="number"
                      min="1"
                      {...register('payment_term_days', {
                        valueAsNumber: true,
                      })}
                      placeholder="Otro"
                      className="w-20 px-2.5 py-1.5 text-xs border border-input-border rounded-md bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 focus:border-transparent transition-all placeholder:text-content-subtle"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* -- Seccion: Lineas del documento -- */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={sectionTitleCls}>Líneas del documento</p>
              <button
                type="button"
                onClick={() => appendLine({ ...EMPTY_LINE })}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
              >
                <Plus size={12} />
                Agregar línea
              </button>
            </div>

            <div className="space-y-1.5">
              {lineFields.map((field, index) => {
                const lineTotal = subtotalDeLinea((watchedLines[index] ?? {}) as any, opcionesDeTotales)

                return (
                  <div
                    key={field.id}
                    className="p-2.5 bg-glass-3 rounded-lg border border-divider space-y-1.5"
                  >
                    {/* Main row: SKU | Name | Unit | Qty | Price | Delete */}
                    <div className="grid grid-cols-12 gap-1.5 items-end">
                      <div className="col-span-2">
                        <label className="block text-xs text-content-muted mb-0.5">
                          SKU
                        </label>
                        <Controller
                          name={`lines.${index}.sku`}
                          control={control}
                          render={({ field }) => (
                            <ProductAutocomplete aria-label="SKU"
                              field="sku"
                              value={field.value}
                              onChange={field.onChange}
                              onSelectProduct={(product) => { fillProductInLine(index, product) }}
                              placeholder="PROD-001"
                              className={inputCls}
                            />
                          )}
                        />
                      </div>
                      <div className="col-span-4">
                        <label className="block text-xs text-content-muted mb-0.5">
                          Nombre <span className="text-danger-solid">*</span>
                        </label>
                        <Controller
                          name={`lines.${index}.name`}
                          control={control}
                          rules={{ required: 'Requerido' }}
                          render={({ field }) => (
                            <ProductAutocomplete aria-label="Nombre"
                              field="name"
                              value={field.value}
                              onChange={field.onChange}
                              onSelectProduct={(product) => { fillProductInLine(index, product) }}
                              placeholder="Nombre del ítem"
                              className={inputCls}
                            />
                          )}
                        />
                        {errors.lines?.[index]?.name && (
                          <p className={errorCls}>
                            {errors.lines[index]?.name?.message}
                          </p>
                        )}
                      </div>
                      <SelectField
                        className="col-span-2"
                        label="Unidad"
                        selectProps={register(`lines.${index}.unit`)}
                      >
                        {UNITS.map((u) => (
                        <option key={u} value={u}>
                        {u}
                        </option>
                        ))}

                      </SelectField>
                      <div className="col-span-1">
                        <label className="block text-xs text-content-muted mb-0.5">
                          Qty
                        </label>
                        <Controller
                          name={`lines.${index}.quantity`}
                          control={control}
                          rules={{ required: true, min: 0 }}
                          render={({ field }) => (
                            <NumericInput aria-label="Qty" {...field} locale={locale} decimals={3} min={0} className={inputCls} />
                          )}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-content-muted mb-0.5">
                          Precio neto
                        </label>
                        <Controller
                          name={`lines.${index}.unit_price`}
                          control={control}
                          rules={{ required: true, min: 0 }}
                          render={({ field }) => (
                            <NumericInput aria-label="Precio neto" {...field} locale={locale} decimals={3} min={0} className={inputCls} />
                          )}
                        />
                      </div>
                      <div className="col-span-1 flex justify-center pb-0.5">
                        {lineFields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="p-1 text-danger-solid hover:text-danger-content hover:bg-danger-surface rounded transition-colors"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Secondary row: Exempt | Discount % | Subtotal */}
                    <div className="flex items-center gap-3 text-xs">
                      <label className="inline-flex items-center gap-1 text-content-muted cursor-pointer select-none">
                        <input
                          type="checkbox"
                          {...register(`lines.${index}.is_tax_exempt`)}
                          className="h-3 w-3 rounded border-divider-strong text-primary focus:ring-primary"
                        />
                        Exento
                      </label>
                      <span className="text-content-subtle">|</span>
                      <label className="text-content-muted flex items-center gap-1">
                        Desc. %
                        <input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          {...register(`lines.${index}.discount_pct`)}
                          placeholder="0"
                          className="w-14 px-1.5 py-0.5 text-xs border border-divider rounded bg-surface focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </label>
                      <span className="ml-auto text-content-muted">
                        Subtotal:{' '}
                        <span className="font-semibold text-content">
                          {formatCLP(lineTotal)}
                        </span>
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Totals panel */}
            <div className="mt-2 flex justify-end">
              <div className="w-64 bg-surface-muted border border-divider rounded-lg p-3 text-xs space-y-1">
                <div className="flex justify-between text-content-muted">
                  <span>Monto Neto</span>
                  <span className="font-medium">{formatCLP(montoNeto)}</span>
                </div>
                {montoExento > 0 && (
                  <div className="flex justify-between text-content-muted">
                    <span>Monto Exento</span>
                    <span>{formatCLP(montoExento)}</span>
                  </div>
                )}
                <div className="flex justify-between text-content-muted">
                  <span>{`${tax.name} (${taxRate}%)`}</span>
                  <span className="font-medium">{formatCLP(montoIVA)}</span>
                </div>
                {isFC && (
                  <div className="flex justify-between text-warning-content-soft">
                    <span>{`${tax.name} Retenido (${taxRate}%)`}</span>
                    <span>-{formatCLP(montoIVA)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-divider-strong pt-1.5 mt-1 text-content">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-sm">
                    {formatCLP(isFC ? total - montoIVA : total)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* -- Seccion: Observaciones -- */}
          <div>
            <p className={sectionTitleCls}>Observaciones</p>
            <textarea aria-label="Observaciones"
              {...register('notes')}
              rows={3}
              placeholder="Notas que se incluirán en documentos generados desde esta plantilla..."
              className={inputCls}
            />
          </div>
        </div>

      </form>
    </Modal>
  )
}
